/**
 * SG Incorporation (Pre) — live UI 문자열 → Word [[if …]] 용 값 정규화.
 * Word 템플릿: `[[if b1_q1]]` / `[[if not b1_q1]]` / `[[if srv_a_1]]` /
 * `[[if has_corporate_shareholder]]` / `[[if b3_q1 == "yes"]]` 등.
 * 문자열 'no' 는 JS 에서 truthy 라 체크가 반대로/미반영되므로 boolean 으로 변환.
 *
 * 또한 Studio 행 키(dir1_director_email …) → Word bare(director_email) 복사,
 * Corp SH 표 d1_* / s1_* → cs_directors[] / cs_shareholders[] 배열 생성.
 */
export function toWordBool(v: unknown): boolean {
  if (v === true || v === 1) return true;
  if (v === false || v === 0 || v == null || v === '') return false;
  const s = String(v).trim().toLowerCase();
  if (['yes', 'true', '1', 'y', 'checked'].includes(s)) return true;
  if (['no', 'false', '0', 'n', 'unchecked', 'na'].includes(s)) return false;
  return Boolean(s);
}

const BOOL_EXACT = new Set([
  'has_corporate_shareholder',
  'has_pep',
  'ihc_is_public',
  'upc_is_public',
  'cs_is_public_listed',
  'has_managing_directors',
  'has_ceos',
  'has_partners',
  'has_others',
  'has_ubos',
  'has_pre_1993_history',
  'is_employment_pass_holder',
  'needs_interpreter',
  'uses_passport',
]);

function isBoolKey(k: string): boolean {
  if (BOOL_EXACT.has(k)) return true;
  if (/^srv_a_\d+$/.test(k)) return true;
  if (/^b[12]_q\d+$/.test(k)) return true;
  if (/^(dir|sh|ubo)\d+_is_pep$/.test(k)) return true;
  return false;
}

/** b3 는 `== "yes"|"no"|"na"` 비교용 문자열 유지 */
function normalizeB3(v: unknown): string {
  const s = String(v ?? '').trim().toLowerCase();
  if (s === 'yes' || s === 'true' || s === '1') return 'yes';
  if (s === 'no' || s === 'false' || s === '0') return 'no';
  if (s === 'na' || s === 'n/a') return 'na';
  return '';
}

function isFilled(v: unknown): boolean {
  return v != null && String(v).trim() !== '';
}

/**
 * dir1_director_email → director_email (첫 행 우선).
 * md1_md_name → md_name, ot1_o_role → o_role 등.
 */
function flattenIndexedPersonRows(out: Record<string, any>) {
  const INDEXED = /^(dir|sh|ubo|pep|md|ceo|pa|ot|sec)(\d+)_(.+)$/;
  const best = new Map<string, { idx: number; val: unknown }>();

  for (const [k, v] of Object.entries(out)) {
    if (k.includes('/') || !isFilled(v)) continue;
    const m = k.match(INDEXED);
    if (!m) continue;
    const idx = Number(m[2]);
    const bare = m[3];
    if (!bare) continue;
    const prev = best.get(bare);
    if (!prev || idx < prev.idx) best.set(bare, { idx, val: v });
  }

  for (const [bare, { val }] of best) {
    if (!isFilled(out[bare])) out[bare] = val;
  }

  // 일부 템플릿은 d_name 별칭 사용
  if (!isFilled(out.d_name) && isFilled(out.director_name)) {
    out.d_name = out.director_name;
  }
}

/** Corp SH Profile — flat dN_* / sN_* → Word 반복 배열 */
function buildCorporateShareholderArrays(out: Record<string, any>) {
  const hasDirs = Array.isArray(out.cs_directors) && out.cs_directors.length > 0;
  if (!hasDirs) {
    const dirs: Record<string, any>[] = [];
    for (let i = 1; i <= 50; i++) {
      if (
        !isFilled(out[`d${i}_name`]) &&
        !isFilled(out[`d${i}_identification_no`]) &&
        !isFilled(out[`d${i}_nationality`])
      ) {
        continue;
      }
      dirs.push({
        no: isFilled(out[`d${i}_no`]) ? out[`d${i}_no`] : i,
        name: out[`d${i}_name`] || '',
        nationality: out[`d${i}_nationality`] || '',
        identification_no: out[`d${i}_identification_no`] || '',
        residential_address: out[`d${i}_residential_address`] || '',
        date_of_appointment: out[`d${i}_date_of_appointment`] || '',
      });
    }
    if (dirs.length) out.cs_directors = dirs;
  }

  const hasShs = Array.isArray(out.cs_shareholders) && out.cs_shareholders.length > 0;
  if (!hasShs) {
    const shs: Record<string, any>[] = [];
    for (let i = 1; i <= 50; i++) {
      if (
        !isFilled(out[`s${i}_name`]) &&
        !isFilled(out[`s${i}_identification_no`]) &&
        !isFilled(out[`s${i}_no_of_shares_and_percentage`])
      ) {
        continue;
      }
      shs.push({
        no: isFilled(out[`s${i}_no`]) ? out[`s${i}_no`] : i,
        name: out[`s${i}_name`] || '',
        nationality: out[`s${i}_nationality`] || '',
        identification_no: out[`s${i}_identification_no`] || '',
        residential_address: out[`s${i}_residential_address`] || '',
        date_of_appointment: out[`s${i}_date_of_appointment`] || '',
        no_of_shares_and_percentage: out[`s${i}_no_of_shares_and_percentage`] || '',
      });
    }
    if (shs.length) out.cs_shareholders = shs;
  }
}

/**
 * formData 를 Word 렌더용으로 복사·정규화.
 * 체크 관련 키는 항상 boolean(또는 b3 문자열)으로 넣고, 미체크 srv/b1/b2 도 false 로 채움.
 */
export function normalizeSgPreFormDataForWord(
  form: Record<string, any>,
): Record<string, any> {
  const out: Record<string, any> = { ...form };

  for (const [k, v] of Object.entries(out)) {
    const bare = k.includes('/') ? k.slice(k.indexOf('/') + 1) : k;
    if (/^b3_q\d+$/.test(bare)) {
      out[k] = normalizeB3(v);
      continue;
    }
    if (isBoolKey(bare)) {
      out[k] = toWordBool(v);
    }
  }

  // Word `if not srv_a_N` / `if not b1_qN` 가 undefined 에 의존하지 않도록 기본 false 채움
  for (let i = 1; i <= 11; i++) {
    const k = `srv_a_${i}`;
    if (!(k in out)) out[k] = false;
    else out[k] = toWordBool(out[k]);
  }
  for (let i = 1; i <= 18; i++) {
    const k = `b1_q${i}`;
    if (!(k in out)) out[k] = false;
    else out[k] = toWordBool(out[k]);
  }
  for (let i = 1; i <= 5; i++) {
    const k = `b2_q${i}`;
    if (!(k in out)) out[k] = false;
    else out[k] = toWordBool(out[k]);
  }
  for (let i = 1; i <= 9; i++) {
    const k = `b3_q${i}`;
    if (!(k in out) || out[k] === '') continue;
    out[k] = normalizeB3(out[k]);
  }

  for (const k of BOOL_EXACT) {
    if (k in out) out[k] = toWordBool(out[k]);
  }

  for (const k of [
    'has_pre_1993_history',
    'is_employment_pass_holder',
    'needs_interpreter',
    'uses_passport',
  ]) {
    if (!(k in out)) out[k] = false;
    else out[k] = toWordBool(out[k]);
  }

  // angular `if recommendation == "recommended"` 등 — 소문자 정규화
  if (out.recommendation) out.recommendation = String(out.recommendation).toLowerCase();
  if (out.approval) out.approval = String(out.approval).toLowerCase();
  if (out.customer_risk_rating) {
    out.customer_risk_rating = String(out.customer_risk_rating).toLowerCase();
    out.risk_rating = out.customer_risk_rating;
  }

  // Form 45 (공통 최신본) — "Dated this [[execution_date]]" 한 칸.
  //   예전 화면은 일/월/년 3칸(execution_day/month/year)이라, 저장분은 합쳐서 채운다.
  if (!isFilled(out.execution_date) && isFilled(out.execution_year)) {
    const y = String(out.execution_year).trim();
    const m = String(out.execution_month ?? '').trim();
    const d = String(out.execution_day ?? '').trim();
    out.execution_date = /^\d{1,2}$/.test(m) && /^\d{1,2}$/.test(d)
      ? `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`
      : [d, m, y].filter(Boolean).join(' ');
  }

  flattenIndexedPersonRows(out);
  buildCorporateShareholderArrays(out);
  buildConnectedPartyArrays(out);

  return out;
}

/**
 * Client Acceptance Form (CDD) — 'Name(s) of All Connected Parties' 반복 목록.
 *
 * 템플릿은 한 줄 반복부를 쓴다:
 *   [[if managing_directors]] • [[md_name]] [[/if managing_directors]]
 * 그런데 매퍼(mapSgIncorporationToPreValues)는 T-Link 의 배열을 화면 키
 * `md{N}_md_name` 으로 **풀어놓기만** 하고 문서용 배열로 다시 묶지 않았다.
 * → 반복할 데이터가 없어 flattenIndexedPersonRows 가 접어 둔 1번 값 하나만 찍혔다.
 *   여기서 화면 키를 다시 배열로 묶어 준다.
 */
function buildConnectedPartyArrays(out: Record<string, any>) {
  const SECTIONS: Array<[string, string, string[]]> = [
    ['managing_directors', 'md', ['md_name']],
    ['ceos', 'ceo', ['ceo_name']],
    ['partners', 'pa', ['p_name']],
    ['others', 'ot', ['o_name', 'o_role']],
  ];
  for (const [section, prefix, fields] of SECTIONS) {
    if (Array.isArray(out[section])) continue;
    const rows: Record<string, any>[] = [];
    for (let i = 1; i <= 30; i++) {
      if (!isFilled(out[`${prefix}${i}_${fields[0]}`])) continue;
      const row: Record<string, any> = {};
      for (const f of fields) row[f] = out[`${prefix}${i}_${f}`] || '';
      rows.push(row);
    }
    if (rows.length) out[section] = rows;
  }
  // 같은 표의 Directors 줄은 [[d_name]] 를 쓴다.
  if (!Array.isArray(out.directors)) {
    const rows: Record<string, any>[] = [];
    for (let i = 1; i <= 50; i++) {
      const name = out[`dir${i}_director_name`];
      if (!isFilled(name)) continue;
      rows.push({ d_name: name, director_name: name });
    }
    if (rows.length) out.directors = rows;
  } else {
    out.directors = out.directors.map((r: any) => ({
      ...r,
      d_name: r?.d_name ?? r?.director_name ?? '',
    }));
  }
}
